import React, {
  useMemo,
  useState,
  useEffect,
  useReducer,
  useContext,
  useRef,
} from 'react'
import Icon from 'components/Icon'
import Page from 'containers/Page'
import ProjectDetails from './components/ProjectDetails'
import ProjectSettings from './components/ProjectSettings'
import ProjectContact from './components/ProjectContact'
import ProjectQuote from './components/ProjectQuote'
import ProjectVoiceBook from './components/ProjectVoiceBook'
import ProjectVideo from './components/ProjectVideo'
import ProjectVote from './components/ProjectVote'
import ProjectProgress from './components/ProjectProgress'
import ProjectDetailsOverview from './components/ProjectDetailsOverview'
import Voice from 'components/Voice'
import VoiceGrid from 'components/VoiceGrid'
import classNames from 'classnames'
import messages from './messages'
import useApi, { request } from 'hooks/useApi'
import useCurrentUser from 'hooks/useCurrentUser'
import { messages as routeMessages } from 'routes'
import useUrl from 'useUrl'
import useLocalStorageState from 'hooks/useLocalStorageState'
import {
  Button,
  Input,
  TextArea,
  Card,
  LoadingFull,
  Chip,
} from '@arnsbogroup/react-voicearchive'
import { FormattedMessage, useIntl } from 'react-intl'
import { useBrand } from 'contexts/BrandContext'
import { ProjectContext, withListener, intents } from 'contexts/ProjectContext'
import {
  Link,
  useNavigate,
  useMatch,
  useLocation,
  useParams,
  useSearchParams,
  Routes,
  Route,
} from 'react-router-dom'
import { ContactCard } from 'components/ContactInfo'
import SharingModal from './components/SharingModal'
import DownloadModal from './components/DownloadModal'
import EditableImg from 'components/EditableImg'
import { useKeyboardShortcut } from '@arnsbogroup/react-voicearchive/lib/hooks'
import { useDrag, useDrop } from 'react-dnd'
import { Helmet } from 'react-helmet'
import genderNames from 'data/genders/messages'
import languageNames from 'data/languages/messages'

const ItemTypes = {
  VOICE: 'voice',
}

export const DraggableVoice = (props) => {
  const [{ isDragging }, drag] = useDrag(() => ({
    type: ItemTypes.VOICE,
    item: props,
    end: (item, monitor) => {
      const dropResult = monitor.getDropResult()
      if (item && dropResult) {
        item.onCharacterChange(dropResult.character)
      }
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
      handlerId: monitor.getHandlerId(),
    }),
  }))
  return (
    <div ref={drag} style={{ opacity: isDragging ? 0.5 : 1 }}>
      <Voice {...props} />
    </div>
  )
}

export const DroppableCharacter = ({ character, children }) => {
  const [{ canDrop, isOver }, drop] = useDrop(() => ({
    accept: ItemTypes.VOICE,
    drop: () => ({ character }),
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  }))

  return (
    <div ref={drop} style={{ opacity: canDrop && isOver ? 0.5 : 1 }} className='break-inside-avoid'>
      {children}
    </div>
  )
}

export const ResetCharacterOnDrop = ({ children }) => {
  const [{ canDrop, isOver }, drop] = useDrop(() => ({
    accept: ItemTypes.VOICE,
    drop: () => ({ character: null }),
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  }))

  return (
    <div ref={drop} style={{ opacity: canDrop && isOver ? 0.5 : 1 }}>
      {children}
    </div>
  )
}

const ProjectPage = () => {
  let [searchParams, setSearchParams] = useSearchParams()
  const location = useLocation()
  const { roles = [] } = useCurrentUser()
  const [viewAsPublic, setViewAsPublic] = useState(
    !roles.includes('coordinator')
  )
  const [characterMap, setCharacterMap] = useState({})
  const [showSharingModal, setShowSharingModal] = useState(
    location?.state?.action == intents.SHARE || searchParams.has('share')
  )

  const [showDownloadModal, setShowDownloadModal] = useState(
    location?.state?.action == intents.DOWNLOAD || searchParams.has('download')
  )

  const [version, setVersion] = useState(0)
  const [isAccepting, setIsAccepting] = useState(false)
  const match = useMatch(location.pathname)
  const navigate = useNavigate()
  const widgetRef = useRef()
  const { formatMessage, locale } = useIntl()
  const brand = useBrand()
  const [votes, setVotes] = useState({})
  const { hashId } = useParams()
  const { project, dispatch } = useContext(ProjectContext)
  const meta = project.meta
  const subPath = useMemo(() => match.pathname.split(`${hashId}/`).pop(), [match])
  const showVote = useMemo(() => [formatMessage(messages.votePath), formatMessage(messages.feedbackPath)].includes(subPath), [subPath])

  const [genders, setGenders] = useState({})
  const [languages, setLanguages] = useState({})
  const availableGenders = useMemo(() => [... new Set(project.suppliers?.map(({ gender }) => gender))], [project.suppliers])
  const availableLanguages = useMemo(() =>
    [
      ...new Set(
        project.suppliers?.flatMap(({ languages }) => languages)
      )
    ]
    .map((iso) => ({
      iso: iso,
      name: formatMessage(languageNames[iso])
    }))
    .sort((a, b) => a.name.localeCompare(b.name)), 
    [project.suppliers]
  )

  useEffect(() => {
    setViewAsPublic(!roles.includes('coordinator'))
  }, [roles])

  useEffect(() => {
    if (location?.state?.saveChanges) {
      project.save()
    }
  }, [location?.state])

  useEffect(() => {
    if (!project.id && !hashId && project.isScoped) {
      project.create()
    } else if (!project.id && hashId) {
      project.fetch(hashId)
    }
  }, [project.id])

  useEffect(() => {
    if (project.id && project.changed) {
      project.save()
    }
  }, [])

  useEffect(() => {
    if (project.loading) {
      return
    }

    if (!hashId && project.hashId) {
      if (location?.state?.action == intents.QUOTE) {
        navigate(`${project.hashId}/${formatMessage(messages.leadPath)}`)
      } else {
        navigate(project.hashId, { replace: true, state: location?.state })
      }
    }

    if (project.hashId) {
      if (project.voiceRequestId && roles.includes('voicerequest')) {
        navigate('voice-book')
      }
    }

    setLanguages(
      project
        .languages
        .concat(project.voices ? Object.values(project.voices)
        .flatMap(voice => voice.languages || []) : [])
        .reduce((memo, language) => ({
          ...memo,
          [language]: true
        }), {})
    )
  }, [project.hashId])

  const anonymous = useMemo(() => (
    roles.includes('coordinator') ||
    project.brand?.theme ||
    [
      formatMessage(messages.votePath),
      formatMessage(messages.voteSubmittedPath)
    ].includes(subPath)
  ), [project, subPath, roles])

  useEffect(() => {
    if (
      !project ||
      !project.hash ||
      widgetRef.current ||
      window.b24Tracker ||
      anonymous
    ) {
      return
    }

    const scriptSrc =
      'https://my.arnsbogroup.com/upload/crm/site_button/loader_8_cffsho.js'
    const script = document.createElement('script')

    script.setAttribute('async', true)
    script.src = scriptSrc + '?' + ((Date.now() / 60000) | 0)

    const onBitrixLiveChat = (event) => {
      event.detail.widget.setUserRegisterData({
        hash: project.hash,
        name: project.hash,
        lastName: project.hash,
        email: project.hash + '@voicefinder.voicearchive.com',
      })

      // event.detail.widget.open() @TODO
      widgetRef.current = event.detail.widget
    }

    window.addEventListener('onBitrixLiveChat', onBitrixLiveChat)
    document.body.appendChild(script)
    widgetRef.current = true

    return () => {
      document.body.removeChild(script)
      window.removeEventListener('onBitrixLiveChat', onBitrixLiveChat)
    }
  }, [project, project.hash, roles, anonymous])

  useEffect(() => {
    const interval = setInterval(() => {
      if (project.version?.length > 0) {
        request(`/rest/v2/project/${project.id}?since=${project.version}`).then(
          (response) => {
            if (response?.data?.version && project.version !== response.data.version) {
              dispatch({ type: 'PATCH', payload: response.data })
            }
          }
        )
      }
    }, 5000)

    return () => {
      window.clearInterval(interval)
    }
  }, [project.hash, project.version])

  const onToggleProduct = (sku) => {
    dispatch({
      type: 'PATCH',
      payload: {
        ...project,
        quotes: project.quotes.map((quote, index) =>
          version != index
            ? quote
            : {
                ...quote,
                products: quote.products.map((product) => ({
                  ...product,
                  selected: sku == product.name && product.optional
                    ? !product.selected
                    : product.selected,
                })),
              }
        ),
      },
    })
  }


  const totalPrice = useMemo(() => (
    project.quotes?.[version]?.products
      .filter(({ selected }) => selected)
      .reduce((sum, { price, total }) => sum + ( price || total), 0) || 0
  ), [project.quotes])

  const onAcceptQuote = () => {
    setIsAccepting(true)
    project.save({
      ...project,
      quotes: Object.assign([], project.quotes, {
        [version]: {
          ...project.quotes[version],
          accepted: true,
        },
      }),
    })
  }

  const onChange = (leafKey, key) => {
    return ({
      target: { value = null } = {},
      currentTarget: { textContent = null } = {},
    }) => {
      dispatch({
        type: 'PATCH',
        payload: {
          ...project,
          intent: intents.QUOTE,
          [leafKey]: {
            ...project[leafKey],
            [key]: typeof value == 'string' ? value : ((value !== undefined && value !== null)  ? value : textContent),
          },
        },
      })
    }
  }

  const voiceGroups = useMemo(() => {
    const map = Object.keys(characterMap).reduce(
      (m, k) => ({ ...m, [k]: [] }),
      {}
    )

    const hasCharacters =
      Object.values(project.voices || {}).filter(({ character }) => character)
        .length || Object.keys(map).length > 0

    const selectedLanguages = Object.entries(languages).filter(([, isSet]) => !!isSet).map(([k]) => k)
    const selectedGenders = Object.entries(genders).filter(([, isSet]) => !!isSet).map(([k]) => k)

    let groups = Object.values(project.voices || {})
      .filter(
        (voice) =>
        (!selectedLanguages.length ? true : selectedLanguages.some((iso) => voice.languages?.includes(iso))) &&
          (!selectedGenders.length ? true : selectedGenders.some((gender) => voice.gender == gender))
      )
      .reduce((memo, voice, i) => {
        const key = voice.character || 'default'

        if (!Array.isArray(memo[key])) {
          memo[key] = []
        }

        let props = {
          Component: viewAsPublic ? Voice : DraggableVoice,
          key: `${key}-${voice.id}-${i}`,
          voice: {
            ...voice,
            demos: (voice.demos || []).filter(({ language }) => {
              if (project.demoLanguages?.length) {
                return project.demoLanguages.includes(language)
              }
              return !selectedLanguages.length ? true : selectedLanguages.includes(language)
            }),
          },
          dispatch: dispatch,
          product: project.category,
          readOnly: true,
          downloadable: project.initatedBy == 'coordinator' || project?.details?.email?.length > 0,
          expandable: true,
          editable: !viewAsPublic,
          onToggle: !showVote && !!project?.quotes.length
            ? () => onToggleProduct(voice.sku)
            : undefined,
          max: hasCharacters ? 1 : undefined,
          selected: project?.quotes[version]?.products?.find(
            ({ name }) => name == voice.sku
          )?.selected,
          onCharacterChange: (character) => {
            dispatch({
              type: 'PATCH_VOICE',
              payload: { ...project.voices[voice.sku], character },
            })
          },
          projectVotes: project.votes,
          vote: votes?.[project.id]?.[voice.id],
          onVote: showVote && !votes?.[project.id]?.[voice.id] ? (vote) => {
            setVotes((prev) => ({...prev, [project.id]: { ...prev[project.id], [voice.id]: vote } }))
          } : undefined,
          onRedoVote: showVote ? () => setVotes((prev) => {
            const next = { ...prev }
            if (next[project.id][voice.id]) {
              delete next[project.id][voice.id]
            }
            return next
          }) : undefined
        }

        if (project?.quotes[version]?.accepted) {
          props = {
            ...props,
            onSelect: undefined,
            onToggle: undefined,
          }
        }

        memo[key].push(props)

        return memo
      }, map)

    if (!anonymous && viewAsPublic && groups.default) {
      if (groups.default.length > 4) {
        groups.default.splice(3, 0, { key: 'contact-card', Component: ContactCard })
      } else {
        groups.default.push({ key: 'contact-card', Component: ContactCard })
      }
    }

    return groups || {}
  }, [project, version, characterMap, viewAsPublic, votes, languages, genders, anonymous])

  const { default: ungroupedVoices = {}, ...groupedVoices } = voiceGroups


  const title = roles.includes('coordinator')
    ? `Editing #${project.leadId || project.hash}`
    : 'Your project'

  return (
    <Page
      childrenProps={{ className: '' }}
      nav={project.isFetched ? !anonymous : false}
      footer={project.isFetched ? !anonymous : false}
    >
      <Helmet>
        <title>{ title }</title>
        <meta property="og:title" content={formatMessage(messages.metaTitle)} />
        <meta property="og:image" content={brand.projectOgImage}/>
        <meta property="og:description" content={formatMessage(messages.metaDescription)} />
        <meta property="og:url" content={useUrl(locale, `${formatMessage(routeMessages.project).replace('/*', '')}/${project.hashId}`)} />
      </Helmet>

      { showSharingModal &&
        <SharingModal
          onClose={() => setShowSharingModal(false)}
          hasEmail={project?.details?.email?.length > 0}
          onSubmitEmail={(payload) => dispatch({
            type: 'SUBMIT_EMAIL',
            payload
          })}
        />
      }

      { showDownloadModal &&
        <DownloadModal
          onClose={() => setShowDownloadModal(false)}
          hasEmail={project?.details?.email?.length > 0}
          onSubmitEmail={(payload) => dispatch({
            type: 'SUBMIT_EMAIL',
            payload
          })}
        />
      }


      {roles.includes('coordinator') && (
        <ProjectSettings
          project={project}
          dispatch={dispatch}
          loading={project.loading}
          onCreateCharacter={() =>
            setCharacterMap((map) => ({
              ...map,
              [window.prompt('Character name:')]: [],
            }))
          }
          onChangeView={() => setViewAsPublic(!viewAsPublic)}
          viewAsPublic={viewAsPublic}
        />
      )}

      <div
        style={{ '--color-primary': project.brand.theme?.primary.default }}
      >
        { project.brand?.theme &&
          <EditableImg
            deleteable
            disabled={viewAsPublic}
            presignedUploadUrl={meta?.s3?.banner}
            onChange={(value) =>
              dispatch({
                type: 'EDIT_BRAND',
                payload: { key: 'banner', value },
              })
            }
          >
            <header
              className="bg-cover bg-center xl:bg-top flex items-center justify-center w-full py-8 2xl:py-20 px-12 bg-primary"
              style={{
                backgroundColor: project.brand.theme?.secondary?.default,
                backgroundImage: `url(${project.brand.banner})`,
              }}
            >
              <EditableImg
                deleteable
                disabled={viewAsPublic}
                presignedUploadUrl={meta?.s3?.logo}
                onChange={(value) =>
                  dispatch({
                    type: 'EDIT_BRAND',
                    payload: { key: 'logo', value },
                  })
                }
              >
                {project.brand?.logo ? (
                  <img className="h-10" src={project.brand.logo} />
                ) : !viewAsPublic ? (
                  <div className="h-10 w-40 bg-gray-200 opacity-50 block" />
                ) : null}
              </EditableImg>
            </header>
          </EditableImg>
        }

        <section
          className={classNames('pb-6 bg-cover bg-center', {  'text-white pt-24': !project.brand?.theme })}
          style={{ backgroundImage: !project.brand?.theme ? 'url(https://voicearchive-branding.s3.eu-central-1.amazonaws.com/banner.jpg)' : undefined }}
        >
          { project?.brand?.theme &&
            <figure className="rounded-full mx-auto w-min mb-6 2xl:mb-10 -my-6 2xl:-my-10 bg-white p-1 z-50">
              <EditableImg
                deleteable
                disabled={viewAsPublic}
                presignedUploadUrl={meta?.s3?.icon}
                onChange={(value) =>
                    dispatch({
                      type: 'EDIT_BRAND',
                      payload: { key: 'icon', value },
                    })
                }
              >
                {project.brand.icon ? (
                  <div
                    style={{ backgroundImage: `url(${project.brand.icon})` }}
                    className="bg-cover bg-center block aspect-square w-20 h-20 rounded-full bg-white"
                  />
                ) : !viewAsPublic ? (
                  <div className="block w-20 h-20 rounded-full flex items-center justify-center bg-gray-200" />
                ) : null}
              </EditableImg>
            </figure>
          }

          <div
            className={classNames(
              [
                'px-4',
                'md:px-0',
                'container',
                'mx-auto',
                'flex',
                'items-center',
                'space-y-2',
                '2xl:space-y-8',
                'space-x-8',
              ],
              {
                'justify-center flex-col': !project.videoSrc,
                'py-2 md:py-16': !project.brand?.banner && viewAsPublic,
              }
            )}
          >
            {project.videoSrc && (
              <div className="w-1/3 flex-shrink-0">
                <ProjectVideo src={project.videoSrc} />
              </div>
            )}

            <div className={classNames('space-y-6', project.videoSrc ? 'text-left' : 'text-center')}>
              { !!project?.hashId?.length &&
                <Chip title="Your project ID">
                  Project ID #{ project.hashId }
                </Chip>
              }
              <h1
                className={classNames('font-medium text-3xl', {
                  'py-1 px-2 border border-gray-300 border-dashed':
                    !viewAsPublic,
                })}
                onBlur={onChange('messages', messages.title.id)}
                dangerouslySetInnerHTML={{
                  __html: formatMessage(messages[{
                    [formatMessage(messages.votePath)]: 'voteTitle',
                    [formatMessage(messages.voteSubmittedPath)]: 'voteSubmittedTitle',
                    [formatMessage(messages.leadPath)]: 'leadTitle',
                    [formatMessage(messages.leadSubmittedPath)]: 'leadSubmittedTitle',
                    [formatMessage(messages.quotePath)]: 'quoteTitle',
                    [formatMessage(messages.quoteSubmittedPath)]: 'quoteSubmittedTitle',
                  }[subPath] ?? 'title'], {
                    name: project.details?.name?.split(' ')?.[0] || '',
                  })
                }}
                contentEditable={!viewAsPublic}
                suppressContentEditableWarning={true}
              />

              <p
                className={classNames('w-full text-lg', {
                  'py-1 px-2 border border-gray-300 border-dashed':
                    !viewAsPublic,
                })}
                onBlur={onChange('messages', messages.subtitle.id)}
                dangerouslySetInnerHTML={{
                  __html: formatMessage(messages[{
                    [formatMessage(messages.feedbackPath)]: 'feedbackSubtitle',
                    [formatMessage(messages.votePath)]: 'voteSubtitle',
                    [formatMessage(messages.voteSubmittedPath)]: 'voteSubmittedSubtitle',
                    [formatMessage(messages.leadPath)]: 'leadSubtitle',
                    [formatMessage(messages.leadSubmittedPath)]: 'leadSubmittedSubtitle',
                    [formatMessage(messages.quotePath)]: 'quoteSubtitle',
                    [formatMessage(messages.quoteSubmittedPath)]: 'quoteSubmittedSubtitle',
                  }[subPath] ?? 'subtitle'], {
                    name: project.details?.name?.split(' ')?.[0] || '',
                  })
                }}
                contentEditable={!viewAsPublic}
                suppressContentEditableWarning={true}
              />
            </div>
          </div>
        </section>

        <section className="bg-gray-50 border-y border-gray-100">
          <div className="container mx-auto">
            <Routes location={location}>
              { viewAsPublic &&
                <>
                  <Route
                    path={formatMessage(messages.votePath)}
                    element={
                      <ProjectVote project={project} votes={votes[project.id]} />
                    }
                  />

                  <Route
                    path={formatMessage(messages.feedbackPath)}
                    element={
                      <ProjectVote project={project} votes={votes[project.id]} external={false} />
                    }
                  />
                </>
              }

              <Route
                path={formatMessage(messages.leadSubmittedPath)}
                element={
                  <div className="py-10 flex items-center justify-center">
                    <ProjectProgress project={project} />
                  </div>
                }
              />

              <Route
                path={formatMessage(messages.leadPath)}
                element={
                  <ProjectDetails
                    onChange={onChange}
                    onSubmit={() => {
                      project.save(null, {
                        then: () => {
                          navigate(formatMessage(messages.leadSubmittedPath))
                        }
                      })
                    }}
                    onCancel={() => navigate('.')}
                    project={project}
                    dispatch={dispatch}
                    loading={project.loading}
                  />
                }
              />

              <Route
                path="voice-book"
                element={
                  <ProjectVoiceBook project={project} />
                }
              />

              { viewAsPublic &&
                <Route
                  index
                  element={
                    <div>
                      <div className="flex flex-col md:flex-row md:justify-between md:items-center my-4 px-4 md:px-0 space-y-4 md:space-y-0">
                        <ProjectProgress project={project} />

                        <div className="space-x-6 flex">
                          <Button
                            className="w-1/2 md:w-auto"
                            variant="text"
                            state="tertiary"
                            onClick={() => setShowSharingModal(true)}
                            icon="share"
                          >
                            Share
                          </Button>

                          { project.initatedBy !== 'coordinator' && !project?.quotes?.length &&
                            <Link to={formatMessage(routeMessages.explore).replace('/*', '/')}>
                              <Button
                                className="w-1/2 md:w-auto"
                                variant="text"
                                state="tertiary"
                                icon="pencil"
                              >
                                Change voices
                              </Button>
                            </Link>
                          }

                          { project.loading || (isAccepting && !project.quotes[version].accepted) ? (
                            <Button state="tertiary" disabled className="w-1/2 md:w-auto">
                              <Icon icon="spinner-third" className="animate-spin mr-1" />
                              <FormattedMessage {...messages.loading} tagName="span" />
                            </Button>
                          ) : (
                            <>
                              {!project?.quotes?.[version]?.accepted && roles.includes('voicerequest') &&
                              <Link to="voice-book">
                                <Button
                                  className="w-1/2 md:w-auto"
                                  state="tertiary"
                                  variant="outline"
                                  title={formatMessage(messages.voiceBookHint)}
                                >
                                  <FormattedMessage {...messages.voiceBook} />
                                </Button>
                              </Link>
                              }

                              {!project.quotes?.length ? (
                                <>
                                  { !project.details?.brief?.length ? (
                                    <Link to={formatMessage(messages.leadPath)} className="w-1/2 md:w-auto">
                                      <Button
                                        className="w-full"
                                        state="tertiary"
                                      >
                                        <FormattedMessage {...messages.startProject} />
                                      </Button>
                                    </Link>
                                  ) : (
                                    <Link to={formatMessage(messages.leadPath)} className="w-1/2 md:w-auto">
                                      <Button
                                        className="w-full"
                                        state="tertiary"
                                      >
                                        <FormattedMessage {...messages.editProject} />
                                      </Button>
                                    </Link>
                                  ) }
                                </>
                              ) : !project.quotes[version].accepted && !isAccepting ? (
                                <Button
                                  state="tertiary"
                                  onClick={onAcceptQuote}
                                  className={classNames('w-1/2 md:w-auto', { 'cursor-not-allowed': totalPrice <= 0 })}
                                  disabled={totalPrice <= 0}
                                  title={totalPrice > 0 ? undefined : formatMessage(messages.missingServerOrVoiceToAcceptProject)}
                                >
                                  <FormattedMessage {...messages.acceptProject}/>
                                </Button>
                              ) : meta?.href?.order ? (
                                <a target="_blank" href={meta.href.order} className="w-1/2 md:w-auto">
                                  <Button className="w-full">
                                    <FormattedMessage
                                      {...messages.openInPortal}
                                      tagName="span"
                                    />
                                    <Icon icon="external-link" />
                                  </Button>
                                </a>
                              ) : null}
                            </>
                          )}
                        </div>
                      </div>

                      <div className="flex">
                        {viewAsPublic && project.details?.brief?.length > 0 && (
                          <div className="basis-0 flex-1 py-4">
                            <ProjectDetailsOverview project={project} />
                          </div>
                        )}

                        {viewAsPublic && !!project.contacts?.length && (
                          <div className="basis-0 flex-1 py-4 space-y-5">
                            <h1 className="text-gray-400 font-medium text-sm space-x-2">
                              <span>Project contacts</span>
                            </h1>
                            <div className="grid sm:grid-cols-2 gap-3">
                              {project.contacts?.map((contact, i) => (
                                <ProjectContact
                                  contact={contact}
                                  key={`${contact.id}-${i}`}
                                />
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  }
                />
              }
            </Routes>

            {viewAsPublic && !showVote && !!project.quotes?.length && (
              <div className="py-4">
                {project.quotes?.length > 1 && version > 0 && (
                  <button
                    onClick={() => setVersion((i) => i - 1)}
                    className="mx-auto w-5/6 block bg-yellow-200 rounded shadow-lg text-center z-10 relative -mt-1"
                  >
                    <span className="text-xs text-gray-600 text-opacity-30 font-medium inline-block pt-3">
                      <FormattedMessage {...messages.showPreviousQuote} />
                    </span>
                  </button>
                )}

                {project.quotes[version] && (
                  <ProjectQuote
                    quote={project.quotes[version]}
                    project={project}
                    onToggleProduct={onToggleProduct}
                  />
                )}

                {project.quotes?.length > 1 &&
                  version < project.quotes.length - 1 && (
                    <button
                      onClick={() => setVersion((i) => i + 1)}
                      className="mx-auto w-5/6 block bg-yellow-200 rounded shadow-lg text-center z-10 relative -mt-1"
                    >
                      <span className="text-xs text-gray-600 text-opacity-30 font-medium inline-block pt-3">
                        <FormattedMessage {...messages.showPreviousQuote} />
                      </span>
                    </button>
                  )}
              </div>
            )}

            {!viewAsPublic && (
              <div className="bg-yellow-50 border border-gray-300 border-dashed flex items-center justify-center my-4 xl:my-10">
                <div className="space-y-4 xl:space-y-8 p-8 text-center">
                  <h1 className="text-xl text-gray-600 font-medium">
                    <FormattedMessage {...messages.viewingProjectAsCoordinatorTitle}/>
                  </h1>
                  <p className="text-gray-500 font-medium">
                    <FormattedMessage {...messages.viewingProjectAsCoordinatorSubtitle}/>
                  </p>
                  {!!project.leadId && (
                      <FormattedMessage {...messages.editContractLead}
                                        values={{
                                          p: chunk => <p className='text-gray-500'>{chunk}</p>,
                                          a: chunk => <a
                                              href={`https://my.arnsbogroup.com/crm/lead/details/${project.leadId}/`}
                                              target="_blank" className="text-blue-600 underline">{chunk}</a>,
                                          leadId: project.leadId,
                                          externalLink: chunk => <Icon icon='external-link' className='text-blue-600'/>
                                        }}/>
                  )}

                  {!!project.dealId && (
                      <FormattedMessage {...messages.editContractDeal}
                                        values={{
                                          p: chunk => <p className='text-gray-500'>{chunk}</p>,
                                          a: chunk => <a
                                              href={`https://my.arnsbogroup.com/crm/deal/details/${project.dealId}/`}
                                              target="_blank" className="text-blue-600 underline">{chunk}</a>,
                                          dealId: project.dealId,
                                          externalLink: chunk => <Icon icon='external-link' className='text-blue-600'/>
                                        }}/>
                  )}
                </div>
              </div>
            )}
          </div>
        </section>

        <main className="container mx-auto px-4 md:pt-6 pb-20 md:px-0">
          <div className="sticky top-0 left-0 w-full py-4 bg-white z-30 space-y-2">
            <div className="w-full flex items-start space-x-0 space-y-2 flex-col md:flex-row md:space-x-6 md:space-y-0">
              { availableGenders.length > 1 &&
                <div className="flex gap-2 items-center">
                  <label className="text-gray-400 font-medium text-xs"><FormattedMessage {...messages.genders}/></label>
                  { availableGenders.map((gender) => (
                    <Chip
                      key={`gender-${gender}`}
                      onClick={() => setGenders((prev) => ({ ...prev, [gender]: !prev[gender] }))}
                      state={genders[gender] ? 'info' : undefined}
                    >
                      <FormattedMessage {...genderNames[gender]} />
                    </Chip>
                  )) }
                </div>
              }

              <div className="flex gap-2 items-center flex-wrap">
                <label className="text-gray-400 font-medium text-xs"><FormattedMessage {...messages.languages}/></label>
                { availableLanguages.map((language) => (
                  <Chip
                    key={`language-${language.iso}`}
                    onClick={() => setLanguages((prev) => ({ ...prev, [language.iso]: !prev[language.iso] }))}
                    state={languages[language.iso] ? 'info' : undefined}
                  >
                    <span className="whitespace-nowrap">
                      { language.name }
                    </span>
                  </Chip>
                )) }
              </div>
            </div>
          </div>
          <VoiceGrid view="MASONRY">
            { Object.entries(groupedVoices).map(([character, entries]) => (
              <DroppableCharacter key={character} character={character}>
                <div
                  className={classNames('border-secondary shadow rounded', {
                    'border-t-4': viewAsPublic,
                    'border border-dashed': !viewAsPublic,
                    'min-h-[17.25rem]': !entries.length,
                  })}
                >
                  <EditableImg
                    key={character}
                    disabled={viewAsPublic}
                    className="text-sm text-secondary font-medium space-x-2 py-3 px-6"
                    onChange={(avatarUrl) => {
                      dispatch({
                        type: 'PATCH',
                        payload: {
                          ...project,
                          voices: Object.entries(project.voices).reduce(
                            (m, [sku, voice]) => ({
                              ...m,
                              [sku]: {
                                ...voice,
                                avatarUrl:
                                  voice.character == character
                                    ? avatarUrl
                                    : voice.avatarUrl,
                              },
                            }),
                            {}
                          ),
                        },
                      })
                    }}
                  >
                    {messages[character] ? (
                      <FormattedMessage
                        {...messages[character]}
                        tagName="span"
                      />
                    ) : (
                      <span>{character}</span>
                    )}
                    {!viewAsPublic && character !== 'UNGROUPED' && (
                      <Icon
                        icon="times"
                        className="hover:text-negative cursor-pointer"
                        onClick={(event) => {
                          event.stopPropagation()
                          dispatch({
                            type: 'PATCH',
                            payload: {
                              ...project,
                              voices: Object.entries(project.voices).reduce(
                                (m, [sku, voice]) => ({
                                  ...m,
                                  [sku]: {
                                    ...voice,
                                    character:
                                      voice.character == character
                                        ? null
                                        : voice.character,
                                  },
                                }),
                                {}
                              ),
                            },
                          })
                          setCharacterMap({})
                        }}
                      />
                    )}
                  </EditableImg>

                  <div
                    className={classNames('space-y-4', {
                      'pb-4 px-4': !viewAsPublic,
                      'p-6': viewAsPublic,
                    })}
                  >
                    {entries.map(({ Component, ...props }) => (
                      <Component {...props} />
                    ))}
                  </div>
                </div>
              </DroppableCharacter>
            ))}
          </VoiceGrid>

          <ResetCharacterOnDrop>
            <div
              className={!!Object.keys(groupedVoices).length ? 'p-2 mt-10' : ''}
            >
              <VoiceGrid view="MASONRY">
                {!Object.keys(ungroupedVoices).length && project.loading
                  ? [...new Array(9)].map((_, i) => (
                      <Card
                        key={`loading-${i}`}
                        className="bg-white break-inside-avoid animate-pulse p-4 space-y-4 border-l-4 border-gray-100"
                      >
                        <div className="bg-gray-50 w-1/4 h-4 block" />
                        <div className="bg-gray-50 w-2/3 h-4 block" />
                        <div className="bg-gray-50 w-1/3 h-4 block" />
                        <div className="flex space-x-2">
                          <div className="bg-gray-50 w-10/12 h-10 block" />
                          <div className="bg-gray-100 w-2/12 h-10 block" />
                        </div>
                      </Card>
                    ))
                  : Object.values(ungroupedVoices).map(
                      ({ Component, ...props }) => <Component {...props} />
                    )}
              </VoiceGrid>
            </div>
          </ResetCharacterOnDrop>
        </main>
      </div>
    </Page>
  )
}

export default ProjectPage
